import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { clearApolloCache } from '@/shared/api';
import { EMITTER_CONSTANTS, KEY_RECOIL, TAB_KEY } from '@/shared/config';
import { useEmitter } from '@/shared/lib/customHooks';
import { callStartHolterStudyProcessing, fetchHolterTasks } from '@/entities/study';
import { checkLimitStudyProcessing, isHolterProcessingAvailable } from '@/shared/lib/aiUtils';
import { zeroPad, auth } from '@/shared/lib';
import { reportTabStatus } from '@/shared/lib/devLog';

import { fetchAIProcessingStatus, selectIsActiveTab, useReportStore } from '@/entities/report';
import emitter from '@/shared/lib/eventEmitter';
import type { RhythmEventsProps } from './index';

/**
 * Coordinates the rhythm-events tab lifecycle, starting AI holter processing for the study profile and gating entry to the tab. Mirrors the active-tab flag into the report store, retries the start call until a socket id exists, raises the simultaneous-study and processing-limit modals when the facility limit is hit, polls the stored AI request ids to unlock the tab, and reacts to the AI-stop and socket-reconnect emitter events.
 *
 * @param props - Tab props whose activeTab value decides when this tab is the active one.
 * @returns An object carrying the tab key, processing and download flags, the report identifiers, the tab-active flag and the visited-tab ref.
 */
const useRhythmEvents = ({ activeTab }: Pick<RhythmEventsProps, 'activeTab'>) => {
  const keyRecoil = KEY_RECOIL.RHYTHM_EVENT;
  const setIsOpenProcessingLimit = useReportStore((s) => s.actions.setIsOpenProcessingLimit);
  const setLimitProcessingStudy = useReportStore((s) => s.actions.setLimitProcessingStudy);
  const isShowTabHeader = useReportStore((s) => s.isShowTabHeader);
  const isLoadedIndexDB = useReportStore((s) => s.isLoadedIndexDB);
  const isCompletedDownloadBeat = useReportStore((s) => s.isCompletedDownloadBeat);

  const isStartedAiProcessing = useReportStore((s) => s.isStartedAiProcessing);
  const setIsStartedAiProcessing = useReportStore((s) => s.actions.setIsStartedAiProcessing);
  const isActiveTab = useReportStore(selectIsActiveTab(keyRecoil));
  const setIsActiveTabAction = useReportStore((s) => s.actions.setIsActiveTab);
  /**
   * Records whether the rhythm-events tab is the active one. Forwards the value or updater to the store action together with the tab key.
   *
   * @param v - The new value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setIsActiveTab = (v: Parameters<typeof setIsActiveTabAction>[1]) =>
    setIsActiveTabAction(keyRecoil, v);
  const [isTabActive, setIsTabActive] = useState(false);

  const hasVisitTab = useRef(false);
  const hasCallStart = useRef<any>();

  // reportInfoState (Recoil, Task 7) is typed {} | IReportInfo - cast to destructure.
  const { studyId, profileId, isNotEdit, studyFid } = useReportStore((s) => s.reportInfo) as any;

  /**
   * Kicks off AI holter processing for the current study profile. Announces the profile as pending, calls the start mutation, refreshes the pending profiles from the holter task list, and on hitting the simultaneous-study limit opens the limit modal with the blocking study fids instead of continuing; otherwise it records the call, raises the processing-limit flag when no slot is free, and marks processing started once the server reports ready.
   *
   * @returns A promise resolving once processing has been requested and the resulting state applied.
   */
  const startHolterStudyProcessing = async () => {
    emitter.emit(EMITTER_CONSTANTS.UPDATE_PENDING_PROFILES, [{ id: profileId }]);
    const result = await callStartHolterStudyProcessing(studyId, profileId);
    const holterTasks = await fetchHolterTasks();
    emitter.emit(EMITTER_CONSTANTS.UPDATE_PENDING_PROFILES, holterTasks?.pendingProfiles || []);
    if (checkLimitStudyProcessing(result)) {
      setLimitProcessingStudy({
        isOpenModal: true,
        limitCount: result?.simultaneousStudyLimit,
        // pre-existing: zeroPad declares a number param; processingStudyFids entries are strings here.
        studyFids: _.map(result?.processingStudyFids, (x: any) => zeroPad(x)),
      });
      return;
    }
    hasCallStart.current = true;
    if (!isHolterProcessingAvailable(holterTasks, studyFid)) {
      setIsOpenProcessingLimit(true);
    }
    if (result?.isReady) {
      setIsStartedAiProcessing(true);
    }
  };

  /**
   * Starts AI holter processing only once a socket connection is available, since the server pushes progress over it. Reschedules itself every 500ms while the auth socket id is missing, then delegates to the start call.
   *
   * @returns Nothing.
   */
  const startHolterStudyProcessingByRetry = () => {
    const socketId = auth.getSocketId();
    console.log('[RhythmEvents] startHolterStudyProcessingByRetry:socketId', { socketId });
    if (!socketId) {
      setTimeout(startHolterStudyProcessingByRetry, 500);
      return;
    }
    startHolterStudyProcessing();
  };

  useEffect(() => {
    const isActive = activeTab === TAB_KEY.ECG_EVENTS;
    // setIsTabActive(false);
    setIsActiveTab(isActive);
    hasVisitTab.current = isActive;
  }, [activeTab]);

  useEffect(() => {
    reportTabStatus(keyRecoil, {
      isActiveTab,
      isStartedAiProcessing,
      hasCallStart: !!hasCallStart.current,
    });
    if (isActiveTab && !isStartedAiProcessing && !hasCallStart.current) {
      // retry after 5s if not ready to start
      startHolterStudyProcessingByRetry();
    }
  }, [isActiveTab, isStartedAiProcessing]);

  /**
   * Decides whether the rhythm-events tab may be entered while an AI processing request started from another tab is still running. Reads the stored request ids for this profile and study from localStorage, unlocks at once when there are none, when they belong to this tab or when the profile is already flagged as processing, otherwise polls each request's AI status, strips the finished ones from storage and activates the tab only if at least one completed.
   *
   * @returns A promise resolving once the tab-active flag has been set.
   */
  const checkCanSwitchTab = async () => {
    try {
      const keyStorage = `requestId-${profileId}-${studyFid}`;
      const storedResult = localStorage.getItem(keyStorage);
      const requestData = JSON.parse(storedResult);
      if (_.isEmpty(requestData)) return setIsTabActive(true);
      const { type, requestId } = requestData?.[0] || {};
      if (!requestId || type === keyRecoil) return setIsTabActive(true);
      const isProcessing = localStorage.getItem(`ai_process-${profileId}`);
      if (JSON.parse(isProcessing)) return setIsTabActive(true);
      const promisses: any[] = [];
      setIsTabActive(false);
      _.forEach(requestData, (x: any) => {
        if (x.requestId) {
          promisses.push(fetchAIProcessingStatus(x.requestId, 'switchTab'));
        }
      });
      const result = await Promise.all(promisses);
      let canSwitchTab = false;
      const updateSuccess: any[] = [];
      _.forEach(result, (x: any, i) => {
        if (x === true) {
          canSwitchTab = true;
          updateSuccess.push(requestData?.[i]);
        }
      });
      _.remove(
        requestData,
        (x: any) => !!_.find(updateSuccess, (y: any) => y.requestId === x.requestId),
      );
      localStorage.setItem(keyStorage, JSON.stringify(requestData));

      if (canSwitchTab) {
        localStorage.setItem(`ai_process-${profileId}`, JSON.stringify(canSwitchTab));
      }

      setIsTabActive(canSwitchTab);
    } catch (error) {
      console.log(error);
      setIsTabActive(false);
    }
    return null;
  };

  useEffect(() => {
    if (isActiveTab) {
      checkCanSwitchTab();
    }
  }, [isActiveTab]);

  useEmitter(
    EMITTER_CONSTANTS.AI_STOP_PROCESSING,
    async (msg: any) => {
      await clearApolloCache();
      if (isActiveTab && !_.isEmpty(msg)) {
        const { requestId } = msg || {};
        const keyStorage = `requestId-${profileId}-${studyFid}`;
        const storedResult = localStorage.getItem(keyStorage);
        const requestData = JSON.parse(storedResult);
        _.remove(requestData, (x: any) => x.requestId === requestId);
        localStorage.setItem(keyStorage, JSON.stringify(requestData));
        if (_.isEmpty(requestData)) {
          localStorage.setItem(`ai_process-${profileId}`, JSON.stringify(true));
          setIsTabActive(true);
        }
      }
    },
    [isActiveTab, profileId, studyFid],
  );

  useEmitter(
    EMITTER_CONSTANTS.SOCKET_RECONNECTED,
    () => {
      if (isTabActive && !isStartedAiProcessing) {
        startHolterStudyProcessingByRetry();
      }
    },
    [isTabActive, isStartedAiProcessing],
  );

  return {
    keyRecoil,
    isStartedAiProcessing,
    isShowTabHeader,
    studyId,
    profileId,
    isNotEdit,
    isLoadedIndexDB,
    isCompletedDownloadBeat,
    isTabActive,
    hasVisitTab,
  };
};

export default useRhythmEvents;
